import { SerializedInstant } from './api/data.types';

/** Serialized water usage data from API */
export interface WaterUsageAPIResponse {
  updated: string;
  /** Summary of how much water has been used, and its cost */
  summary: WaterUsageAmountsAPI;
  /** Water usage measurements */
  items: Array<{
    timeStamp: string;
    previousTimestamp?: string;
    date: string;
    usage: WaterUsageAmountsAPI;
  }>;
  /** The period from which water usage data is available */
  period: {
    start: string;
    end: string;
  };
  /** Prices m3 per month */
  usagePrices: UsagePrices | null;
}

/** Serialized form of WaterUsageAmounts, as received from the API */
export interface WaterUsageAmountsAPI
  extends Omit<
    WaterUsageAmounts,
    | 'previousTimeStampCold'
    | 'previousTimeStampWarm'
    | 'timeStampCold'
    | 'timeStampWarm'
  > {
  previousTimeStampCold?: SerializedInstant;
  previousTimeStampWarm?: SerializedInstant;
  timeStampCold?: SerializedInstant;
  timeStampWarm?: SerializedInstant;
}

export interface UsagePrices {
  warmWater: number;
  coldWater: number;
}

/** De-serialized water usage data */
export interface WaterUsageData {
  updated: Date;
  /** Summary of how much water has been used, and its cost */
  summary: WaterUsageAmounts;
  /** Water usage measurements */
  items: WaterUsageMeasurement[];
  /** The period from which water usage data is available */
  period: WaterUsagePeriod;
}

export interface WaterUsagePeriod {
  start: Date;
  end: Date;
}

export interface WaterUsageMeasurement {
  /** Date and time when the measurement was checked and stored in the database */
  timeStamp: Date;
  previousTimestamp?: Date;
  /** Date of the measurements done */
  date: Date;
  usage: WaterUsageAmounts;
}

export interface WaterUsageAmounts {
  /** Warm water usage in liters */
  warm: number;
  /** Cold water usage in liters */
  cold: number;
  /** Total water usage (cold + warm) in liters */
  total: number;
  /** Timestamp of the previous cold water meter read */
  previousTimeStampCold?: Date;
  /** Timestamp of the previous warm water meter read */
  previousTimeStampWarm?: Date;
  /** Timestamp of the cold water meter read */
  timeStampCold?: Date;
  /** Timestamp of the warm water meter read */
  timeStampWarm?: Date;
  costs?: WaterCosts;
}

interface WaterCosts {
  /** Cost of warm water usage in euros */
  warm: number;
  /** Cost of cold water usage in euros */
  cold: number;
  /** Total cost of water usage in euros */
  total: number;
}

export enum WaterUsageUnit {
  LITER = 'LITER',
  // M3 = 'M3' // TODO: M3 does not seem to work in the API
}
